import { z } from 'zod';
import { HexColorSchema } from './common.js';
// Slugs match the project-key style: lowercase letters, digits, hyphens.
// Two characters minimum so it doesn't collide with single-letter dev fixtures.
const SlugSchema = z.string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters, digits and hyphens');
// Phase 3 starter-payload shapes.
//
// These mirror the starter JSON files (containers/tags/rules) the Vault
// pack ships, with the route layer doing name->ULID resolution between
// groups. Containers + tags carry name as the cross-reference key.
// Tagging rules reference containers and tags BY NAME; the route swaps
// them for the freshly-inserted ULIDs before insert.
//
// The validator at apps/api/scripts/validate-starter.ts exercises the
// same shapes ahead of an actual POST.
const StarterContainerSchema = z.object({
    name: z.string().min(1).max(120),
    slug: z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9-]*$/).optional(),
    description: z.string().max(1000).nullable().optional(),
    color: HexColorSchema.nullable().optional(),
    icon: z.string().max(32).nullable().optional(),
    // Importer markers — interpreted by the route, not stored verbatim.
    is_default_for_unrouted: z.boolean().optional(),
    _seeded_existing: z.boolean().optional(),
});
const StarterTagSchema = z.object({
    name: z.string().min(1).max(100).regex(/^[^,]+$/, 'no commas allowed'),
    color: HexColorSchema,
    description: z.string().max(500).nullable().optional(),
});
// Conditions inline — match the tagging-rule schema but no need to
// import to avoid a circular dependency.
const StarterRuleConditionSchema = z.object({
    field: z.enum([
        'document_name', 'title', 'content',
        'metadata_field', 'custom_property',
        'file_type', 'date', 'tag',
    ]),
    fieldKey: z.string().optional(),
    operator: z.enum([
        'contains', 'equals', 'starts_with', 'ends_with',
        'regex_match', 'before', 'after', 'is_empty', 'is_not_empty',
    ]),
    value: z.string().optional(),
});
const StarterRuleSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    matchLogic: z.enum(['all', 'any']).default('all'),
    conditions: z.array(StarterRuleConditionSchema).min(1).max(20),
    // tagsToApply / tagsToRemove are NAMES here (resolved to ULIDs by
    // the route after tags land). containerToAssign is a NAME too.
    tagsToApply: z.array(z.string()).default([]),
    tagsToRemove: z.array(z.string()).default([]),
    containerToAssign: z.string().min(1).max(120).nullable().optional(),
    triggers: z.array(z.enum(['on_import', 'on_metadata_change', 'on_reprocess', 'manual']))
        .default(['on_import', 'manual']),
    priority: z.number().int().min(0).max(1000).default(10),
    enabled: z.boolean().default(true),
});
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const OrganizationCreateSchema = z.object({
    // Slug is optional on create — backend derives from name when omitted.
    slug: SlugSchema.optional(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    plan: z.string().max(32).nullable().optional(),
    // Phase 3 starter payload. All optional — POST /orgs without these
    // still creates an org with the minimum bootstrap (Inbox + Sidecars
    // + built-in file workflows).
    starter: z.object({
        containers: z.array(StarterContainerSchema).max(100).optional(),
        tags: z.array(StarterTagSchema).max(500).optional(),
        rules: z.array(StarterRuleSchema).max(100).optional(),
    }).optional(),
}).strict();
// .strip() restores Zod's default unknown-key tolerance for PATCH —
// CTSK-445 made Create strict, and .partial()/.extend() would otherwise
// inherit `unknownKeys: "strict"`. Patch semantics deliberately
// unchanged.
export const OrganizationPatchSchema = OrganizationCreateSchema.partial().extend({
    // Owners can deactivate an org without deleting it.
    isActive: z.boolean().optional(),
    // CTSK-283 — per-org accent color (#RRGGBB). null clears the
    // override back to the app default; omit to leave unchanged.
    themeColor: HexColorSchema.nullable().optional(),
}).strip();
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop). Note: .strict() sits on the
 * inner z.object — the .refine()s below wrap it in ZodEffects.
 */
export const OrganizationMembershipCreateSchema = z.object({
    // Caller may provide EITHER the user's ULID or their email. The
    // route handler resolves email → userId via the users table.
    // Lets the UI add by typed email without a separate lookup; the
    // old userId path stays valid for programmatic callers.
    userId: z.string().min(1).max(26).optional(),
    email: z.string().email().optional(),
    // Same dual-resolution for role: ULID or name. Both DEFAULT_ROLES
    // names (owner / operator / reviewer / contributor / viewer) and
    // custom role names resolve at the route. UI-friendly path.
    roleId: z.string().min(1).max(26).optional(),
    roleName: z.string().min(1).max(64).optional(),
    isOwner: z.boolean().default(false),
})
    .strict()
    .refine((b) => !!b.userId || !!b.email, { message: 'Provide userId or email' })
    .refine((b) => !!b.roleId || !!b.roleName, { message: 'Provide roleId or roleName' });
export const OrganizationMembershipPatchSchema = z.object({
    roleId: z.string().min(1).max(26).optional(),
    isOwner: z.boolean().optional(),
});
// CTSK-242 — atomic org transfer. Body for
// POST /orgs/:id/memberships/:userId/transfer. Same dual role
// resolution (ULID or name) as OrganizationMembershipCreateSchema so
// both the UI (role picker knows names) and programmatic callers
// (know ULIDs) can drive it without a lookup round-trip.
export const OrganizationMembershipTransferSchema = z.object({
    targetOrgId: z.string().min(1).max(26),
    targetRoleId: z.string().min(1).max(26).optional(),
    targetRoleName: z.string().min(1).max(64).optional(),
    // Default false = move (source membership row is deleted). True =
    // copy-style transfer that leaves the source membership in place
    // (ticket scope: keepOldMembership?: false).
    keepOldMembership: z.boolean().default(false),
}).refine((b) => !!b.targetRoleId || !!b.targetRoleName, {
    message: 'Provide targetRoleId or targetRoleName',
});
export const SwitchOrgSchema = z.object({
    orgId: z.string().min(1).max(26),
});
//# sourceMappingURL=organization.js.map